import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { can } from '../components/PaymentActions';

const usd = (n) => `$${Number(n || 0).toFixed(2)}`;

const STATUS_TABS = ['pending', 'approved', 'rejected'];
const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-700',
};

// Approve = credit the wallet (money-in). CONFIRM-to-authorize.
const ApproveModal = ({ item, onClose, onDone }) => {
  const [note, setNote] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const ready = typed.trim().toUpperCase() === 'CONFIRM';
  const submit = async () => {
    setBusy(true);
    try {
      const res = await adminApi.approveZelleFunding(item.id, note);
      toast.success(`Credited ${usd(res.creditedUsd)} to ${item.email} · new balance ${usd(res.balanceAfterUsd)}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Approve failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-gray-600 bg-opacity-75 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mt-10 sm:mt-20 p-5">
        <h3 className="text-lg font-bold text-gray-900">Approve Zelle funding — {usd(item.amountUsd)}</h3>
        <p className="text-sm text-gray-500 mt-0.5 break-all">{item.email}</p>
        <div className="mt-3 rounded-md bg-green-50 border border-green-200 p-3 text-xs text-green-800">
          Confirm the Zelle transfer of <strong>{usd(item.amountUsd)}</strong> (memo <strong>{item.memoCode}</strong>{item.senderName ? `, from ${item.senderName}` : ''}) actually landed in the bank before approving. This <strong>credits the customer's wallet</strong> and cannot be auto-reversed.
        </div>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Note (optional)</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. matched bank deposit 6/22"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px]" />
        </label>
        <label className="block mt-3 text-sm">
          <span className="text-gray-700">Type <strong>CONFIRM</strong> to credit the wallet</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="CONFIRM"
            className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 min-h-[44px] uppercase" />
        </label>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} disabled={busy} className="min-h-[44px] px-4 py-2 rounded-md border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={!ready || busy} className="min-h-[44px] px-4 py-2 rounded-md text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-40">
            {busy ? 'Working…' : 'Approve + credit'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ZelleFunding = () => {
  const { user } = useAuth();
  const role = user?.role || 'viewer';
  const isFinance = can(role, 'finance');
  const isSupport = can(role, 'support');
  const [status, setStatus] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [approveItem, setApproveItem] = useState(null);

  const load = useCallback(async (s) => {
    setLoading(true); setError(null);
    try { setData(await adminApi.getZelleFundingRequests({ status: s })); }
    catch (e) {
      const msg = e.response?.status === 403 ? 'Requires the support role.' : (e.response?.data?.message || e.message || 'Failed to load');
      setError(msg); if (e.response?.status !== 403) toast.error(msg);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isSupport) load(status); else setError('Requires the support role.'); }, [status, load, isSupport]);

  const items = data?.items || [];
  const onDone = () => { setApproveItem(null); load(status); };

  const reject = async (it) => {
    const note = window.prompt(`Reject funding request from ${it.email} (${usd(it.amountUsd)})? Optional note:`, '');
    if (note === null) return;
    try { await adminApi.rejectZelleFunding(it.id, note); toast.success('Request rejected'); load(status); }
    catch (e) { toast.error(e.response?.data?.message || 'Reject failed'); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Zelle Funding</h1>
          <p className="text-sm text-gray-500">Verify the Zelle transfer landed, then approve to credit the customer's wallet · your role: <span className="font-medium">{role}</span></p>
        </div>
        <button onClick={() => load(status)} disabled={loading || !isSupport}
          className="inline-flex items-center justify-center min-h-[44px] px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">
          <ArrowPathIcon className={`h-5 w-5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        {STATUS_TABS.map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize ${status === s ? 'bg-indigo-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
            {s}{status === s && data ? ` (${data.count})` : ''}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 mb-4 flex items-center">
          <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" /><span className="text-sm">{error}</span>
        </div>
      )}
      {loading && !data && <div className="text-center text-gray-500 py-12">Loading…</div>}
      {data && items.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500">No {status} funding requests.</div>
      )}

      {items.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Customer', 'Amount', 'Memo', 'Sender / Zelle ref', 'Submitted', 'Balance', 'Action'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 break-all">{it.customerName || '—'}<div className="text-xs text-gray-500">{it.email}</div></td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">{usd(it.amountUsd)}</td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-700">{it.memoCode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{it.senderName || '—'}{it.zelleReference ? <div className="text-gray-400">ref {it.zelleReference}</div> : null}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{it.createdAt ? new Date(it.createdAt).toLocaleString() : ''}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{usd(it.currentBalanceUsd)}</td>
                  <td className="px-4 py-3">
                    {it.status === 'pending' ? (
                      <div className="flex gap-2">
                        {isFinance && <button onClick={() => setApproveItem(it)} className="min-h-[36px] px-3 py-1.5 rounded text-xs font-semibold text-white bg-green-600 hover:bg-green-700">Approve</button>}
                        <button onClick={() => reject(it)} className="min-h-[36px] px-3 py-1.5 rounded text-xs font-semibold text-white bg-red-600 hover:bg-red-700">Reject</button>
                      </div>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_BADGE[it.status]}`}>{it.status}{it.reviewedBy ? ` · ${it.reviewedBy}` : ''}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {approveItem && <ApproveModal item={approveItem} onClose={() => setApproveItem(null)} onDone={onDone} />}
    </div>
  );
};

export default ZelleFunding;
